/**
 * LandingResultPanel — shows the post-simulation results summary.
 * All values come from the actual simulation landing result.
 */

import { Panel, Stat, StatGrid, SectionTitle, Badge } from "@/components/ui"
import type { LandingResult } from "@/lib/physics"
import { formatSpeed, formatDistance, formatTime } from "@/lib/format"

interface LandingResultPanelProps {
  result: LandingResult | null
  controller: string | null
}

function criterionStatus(passed: boolean, label: string) {
  return (
    <span className={`text-xs font-mono ${
      passed ? "text-ok-500" : "text-danger-500"
    }`}>
      {passed ? "✓" : "✗"} {label}
    </span>
  )
}

export function LandingResultPanel({ result, controller }: LandingResultPanelProps) {
  if (!result) {
    return (
      <Panel title="Landing Result" padded={false}>
        <div className="p-3 h-[280px] flex flex-col items-center justify-center text-muted">
          <p>No simulation results yet.</p>
          <p className="text-[0.625rem] text-dim">Run a simulation to see the landing outcome.</p>
        </div>
      </Panel>
    )
  }

  const criteria = [
    criterionStatus(Math.abs(result.touchdownVz) <= 5, `Vertical speed ≤ 5 m/s (${Math.abs(result.touchdownVz).toFixed(2)})`),
    criterionStatus(Math.abs(result.touchdownVx) <= 2, `Horizontal speed ≤ 2 m/s (${Math.abs(result.touchdownVx).toFixed(2)})`),
    criterionStatus(result.horizontalError <= 10, `Horizontal error ≤ 10 m (${result.horizontalError.toFixed(2)})`),
  ]

  return (
    <Panel title="Landing Result" padded={false}>
      <div className="p-3 space-y-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[0.625rem] text-muted uppercase tracking-wider">Outcome</span>
          <Badge
            variant={result.landingSuccess ? "safe" : "danger"}
          >
            {result.landingSuccess ? "LANDED" : "IMPACT"}
          </Badge>
        </div>

        <StatGrid cols={4}>
          <Stat label="Touchdown Speed" value={formatSpeed(result.touchdownSpeed)} accent="blue" />
          <Stat label="Touchdown VX" value={formatSpeed(result.touchdownVx)} accent="blue" />
          <Stat label="Touchdown VZ" value={formatSpeed(result.touchdownVz)} accent="blue" />
          <Stat label="Horizontal Error" value={formatDistance(result.horizontalError)} accent="cyan" />
          <Stat label="Controller" value={controller ?? "—"} accent="cyan" />
          <Stat label="Impact Time" value={formatTime(result.impactTime)} accent="cyan" />
        </StatGrid>

        <div className="border-t border-border pt-3">
          <SectionTitle>Landing Criteria</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {criteria.map((c, i) => (
              <div key={i} className="text-xs text-ink leading-relaxed">
                {c}
              </div>
            ))}
          </div>
        </div>

        {!result.landingSuccess && (
          <div className="bg-danger-500/10 border border-danger-500/30 rounded-sm p-3">
            <SectionTitle>Why It Failed</SectionTitle>
            <ul className="text-sm text-danger-400 space-y-1 list-disc list-inside">
              {Math.abs(result.touchdownVz) > 5 && (
                <li>Excessive vertical touchdown speed ({Math.abs(result.touchdownVz).toFixed(2)} m/s &gt; 5 m/s)</li>
              )}
              {Math.abs(result.touchdownVx) > 2 && (
                <li>Excessive horizontal touchdown speed ({Math.abs(result.touchdownVx).toFixed(2)} m/s &gt; 2 m/s)</li>
              )}
              {result.horizontalError > 10 && (
                <li>Outside landing target zone ({result.horizontalError.toFixed(2)} m &gt; 10 m)</li>
              )}
              {!result.landingSuccess &&
                Math.abs(result.touchdownVz) <= 5 &&
                Math.abs(result.touchdownVx) <= 2 &&
                result.horizontalError <= 10 && (
                  <li>Attitude or angular rate outside limits at touchdown</li>
                )}
            </ul>
          </div>
        )}
      </div>
    </Panel>
  )
}
